import React, { useState, useEffect } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import axios from '../../../../../services/AxiosInstance';
import InvoicesTable from '../../Invoices/InvoicesTable';
import PageHeader from '../../../../layouts/PageHeader';
import { getApiUrl } from '../../../../../utils/ApiUtils';
import { isSweden as isSwedenCountry } from '../../../../../utils/vatTreatment';
import { Button, Spinner, Alert } from 'react-bootstrap';
import { Plus, FileText, ArrowLeft, Edit3, Trash2, AlertTriangle, Users } from 'lucide-react';
import { Section, Facts, Fact, SectionEmpty } from '../../Personell/EmployeeDetailParts';
import { AddressModal, ContactModal, EditCustomerModal } from './CustomerDetailModals';
import './customer-detail.css';
import { toast } from 'sonner';
import swal from 'sweetalert';

const ViewCustomerDetailed = () => {
  const { companyId, customerId } = useParams();
  const history = useHistory();
  const [customer, setCustomer] = useState(null);

  const [showEditModal, setShowEditModal] = useState(false);
  const [editForm, setEditForm] = useState({});

  const [invoices, setInvoices] = useState([]);
  const [statistics, setStatistics] = useState({
    totalEarned: 0,
    invoiceCount: 0,
    pendingInvoices: 0,
    overdueInvoices: 0
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Modal states
  const [showAddressModal, setShowAddressModal] = useState(false);
  const [showContactModal, setShowContactModal] = useState(false);
  const [editingAddressId, setEditingAddressId] = useState(null);
  const [editingContactId, setEditingContactId] = useState(null);
  const [addressForm, setAddressForm] = useState({
    address_type: 'billing',
    street1: '',
    street2: '',
    postalcode: '',
    city: '',
    country: 'Sverige'
  });
  const [contactForm, setContactForm] = useState({
    contact_name: '',
    role: '',
    contact_email: '',
    contact_phone: ''
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError(null);

      try {
        // Fetch customer details and invoices in parallel
        const [customerResponse, invoicesResponse] = await Promise.all([
          axios.get(getApiUrl(`${companyId}/sales/customers/${customerId}`), { withCredentials: true }),
          axios.get(getApiUrl(`${companyId}/sales/customers/${customerId}/invoices`), { withCredentials: true })
        ]);

        setCustomer(customerResponse.data);
        setInvoices(invoicesResponse.data.data);
        calculateStatistics(invoicesResponse.data.data);
      } catch (error) {
        console.error('Error fetching data:', error);
        setError('Kunde inte hämta kunddata. Försök igen senare.');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [companyId, customerId]);

  // Figures come from the rows the customer-invoices endpoint returns:
  // status is Draft/Sent/Partially_Paid/Paid/Cancelled/Credited, the amount is
  // total_with_tax and the due date is `deadline`. Drafts and cancelled
  // invoices are not revenue; a credit note counts against it.
  const calculateStatistics = (rows) => {
    const today = new Date().toISOString().slice(0, 10);
    const counted = rows.filter((inv) => !['Draft', 'Cancelled'].includes(inv.status));
    const totalEarned = counted.reduce((sum, inv) => {
      const amount = Math.abs(parseFloat(inv.total_with_tax) || 0);
      return sum + (inv.invoice_type === 'credit_note' ? -amount : amount);
    }, 0);
    const open = rows.filter((inv) =>
      inv.invoice_type !== 'credit_note' && ['Sent', 'Partially_Paid'].includes(inv.status));
    setStatistics({
      totalEarned,
      invoiceCount: rows.length,
      pendingInvoices: open.length,
      overdueInvoices: open.filter((inv) => inv.deadline && inv.deadline < today).length,
    });
  };

  // Open address modal for adding new address
  const handleOpenAddAddress = () => {
    setEditingAddressId(null);
    setAddressForm({
      address_type: 'billing',
      street1: '',
      street2: '',
      postalcode: '',
      city: '',
      country: 'Sverige'
    });
    setShowAddressModal(true);
  };

  // Open address modal for editing existing address
  const handleOpenEditAddress = (address) => {
    setEditingAddressId(address.address_id);
    setAddressForm({
      address_type: address.address_type || 'billing',
      street1: address.street1 || '',
      street2: address.street2 || '',
      postalcode: address.postalcode || '',
      city: address.city || '',
      country: address.country || 'Sverige'
    });
    setShowAddressModal(true);
  };

  // Handle address form submission (add or edit)
  const handleAddressSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      let response;
      if (editingAddressId) {
        // Update existing address
        response = await axios.put(
          getApiUrl(`${companyId}/sales/customers/${customerId}/addresses/${editingAddressId}`),
          addressForm,
          { withCredentials: true }
        );

        // Update customer data with updated address
        setCustomer(prev => ({
          ...prev,
          addresses: prev.addresses.map(addr =>
            addr.address_id === editingAddressId ? { ...addr, ...response.data } : addr
          )
        }));
      } else {
        // Add new address
        response = await axios.post(
          getApiUrl(`${companyId}/sales/customers/${customerId}/addresses`),
          addressForm,
          { withCredentials: true }
        );

        // Update customer data with new address
        setCustomer(prev => ({
          ...prev,
          addresses: [...(prev.addresses || []), response.data]
        }));
      }

      // Reset form and close modal
      setAddressForm({
        address_type: 'billing',
        street1: '',
        street2: '',
        postalcode: '',
        city: '',
        country: 'Sverige'
      });
      setEditingAddressId(null);
      setShowAddressModal(false);
    } catch (error) {
      toast.error(editingAddressId ? 'Kunde inte uppdatera adress. Försök igen.' : 'Kunde inte lägga till adress. Försök igen.');
    } finally {
      setSubmitting(false);
    }
  };

  // Open contact modal for adding new contact
  const handleOpenAddContact = () => {
    setEditingContactId(null);
    setContactForm({
      contact_name: '',
      role: '',
      contact_email: '',
      contact_phone: ''
    });
    setShowContactModal(true);
  };

  // Open contact modal for editing existing contact
  const handleOpenEditContact = (contact) => {
    setEditingContactId(contact.contact_id);
    setContactForm({
      contact_name: contact.contact_name || '',
      role: contact.role || '',
      contact_email: contact.contact_email || '',
      contact_phone: contact.contact_phone || ''
    });
    setShowContactModal(true);
  };

  // Handle contact form submission (add or edit)
  const handleContactSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      let response;
      if (editingContactId) {
        // Update existing contact
        response = await axios.put(
          getApiUrl(`${companyId}/sales/customers/${customerId}/contacts/${editingContactId}`),
          contactForm,
          { withCredentials: true }
        );

        // Update customer data with updated contact
        setCustomer(prev => ({
          ...prev,
          contacts: prev.contacts.map(c =>
            c.contact_id === editingContactId ? { ...c, ...response.data } : c
          )
        }));
      } else {
        // Add new contact
        response = await axios.post(
          getApiUrl(`${companyId}/sales/customers/${customerId}/contacts`),
          contactForm,
          { withCredentials: true }
        );

        // Update customer data with new contact
        setCustomer(prev => ({
          ...prev,
          contacts: [...(prev.contacts || []), response.data]
        }));
      }

      // Reset form and close modal
      setContactForm({
        contact_name: '',
        role: '',
        contact_email: '',
        contact_phone: ''
      });
      setEditingContactId(null);
      setShowContactModal(false);
    } catch (error) {
      toast.error(editingContactId ? 'Kunde inte uppdatera kontakt. Försök igen.' : 'Kunde inte lägga till kontaktperson. Försök igen.');
    } finally {
      setSubmitting(false);
    }
  };

  // Handle address deletion
  const handleDeleteAddress = async (addressId) => {
    const willDelete = await swal({
      title: 'Ta bort adress',
      text: 'Är du säker på att du vill ta bort denna adress?',
      icon: 'warning',
      buttons: ['Avbryt', 'Ta bort'],
      dangerMode: true,
    });

    if (!willDelete) return;

    try {
      await axios.delete(
        getApiUrl(`${companyId}/sales/customers/${customerId}/addresses/${addressId}`),
        { withCredentials: true }
      );

      // Update customer data by removing the deleted address
      setCustomer(prev => ({
        ...prev,
        addresses: prev.addresses.filter(addr => addr.address_id !== addressId)
      }));

      toast.success('Adressen har tagits bort.');
    } catch (error) {
      toast.error('Kunde inte ta bort adressen. Försök igen.');
    }
  };

  // Handle contact deletion
  const handleDeleteContact = async (contactId) => {
    const willDelete = await swal({
      title: 'Ta bort kontaktperson',
      text: 'Är du säker på att du vill ta bort denna kontaktperson?',
      icon: 'warning',
      buttons: ['Avbryt', 'Ta bort'],
      dangerMode: true,
    });

    if (!willDelete) return;

    try {
      await axios.delete(
        getApiUrl(`${companyId}/sales/customers/${customerId}/contacts/${contactId}`),
        { withCredentials: true }
      );

      // Update customer data by removing the deleted contact
      setCustomer(prev => ({
        ...prev,
        contacts: prev.contacts.filter(c => c.contact_id !== contactId)
      }));

      toast.success('Kontaktpersonen har tagits bort.');
    } catch (error) {
      toast.error('Kunde inte ta bort kontaktpersonen. Försök igen.');
    }
  };

  const handleEditCustomer = () => {
    const country = customer.country || 'Sverige';
    setEditForm({
      type: customer.type,
      name: customer.name || '',
      first_name: customer.first_name || '',
      last_name: customer.last_name || '',
      email: customer.email || '',
      phone: customer.phone || '',
      orgnumber: customer.orgnumber || '',
      vatnumber: customer.vatnumber || '',
      country,
      // Defensive: only honor stored bygg flag when country is SE. Stale
      // data from before the form redesign could have bygg=1 set on a
      // foreign customer; we never display the conflicting state.
      is_construction_business:
        isSwedenCountry(country) &&
        Boolean(Number(customer.is_construction_business || 0)),
      payment_terms: customer.payment_terms || 30
    });
    setShowEditModal(true);
  };

  // Add this function to handle form submission
  const handleEditSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    const country = editForm.country || 'Sverige';
    const isConstruction = isSwedenCountry(country) && Boolean(editForm.is_construction_business);
    const payload = {
      ...editForm,
      country,
      is_construction_business: isConstruction ? 1 : 0
    };

    try {
      const response = await axios.put(
        getApiUrl(`${companyId}/sales/customers/${customerId}`),
        payload,
        { withCredentials: true }
      );

      setCustomer(response.data);
      setShowEditModal(false);
      toast.success('Kunduppgifterna har uppdaterats.');
    } catch (error) {
      toast.error('Kunde inte uppdatera kunden. Försök igen.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: '400px' }}>
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Laddar...</span>
        </Spinner>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container-fluid">
        <Alert variant="danger" className="text-center">
          <AlertTriangle size={24} className="mb-2" />
          <h5>Ett fel uppstod</h5>
          <p>{error}</p>
          <Button variant="outline-danger" onClick={() => history.goBack()}>
            <ArrowLeft size={16} className="me-2" />
            Gå tillbaka
          </Button>
        </Alert>
      </div>
    );
  }

  if (!customer) {
    return (
      <Alert variant="warning" className="text-center">
        <h5>Kunden hittades inte</h5>
        <Button variant="outline-warning" onClick={() => history.goBack()}>
          <ArrowLeft size={16} className="me-2" />
          Gå tillbaka
        </Button>
      </Alert>
    );
  }

  const isCompany = customer.type === 'company';
  const customerName = isCompany
    ? (customer.name || 'Namnlös kund')
    : `${customer.first_name || ''} ${customer.last_name || ''}`.trim() || 'Namnlös kund';
  const addresses = customer.addresses || [];
  const contacts = customer.contacts || [];
  const formatSek = (v) => new Intl.NumberFormat('sv-SE', {
    style: 'currency', currency: 'SEK', minimumFractionDigits: 0, maximumFractionDigits: 0,
  }).format(v);
  const newInvoice = () => history.push(`/${companyId}/create-invoices?customerId=${customerId}`);
  const addressKind = (type) => (
    type === 'billing' ? 'Fakturaadress' : type === 'shipping' ? 'Leveransadress' : 'Annan adress'
  );
  const closeAddressModal = () => { setShowAddressModal(false); setEditingAddressId(null); };
  const closeContactModal = () => { setShowContactModal(false); setEditingContactId(null); };

  return (
    <>
      <PageHeader
        backTo={{ to: `/${companyId}/customers`, label: 'Alla kunder' }}
        icon={Users}
        title={customerName}
        subtitle={isCompany ? 'Företag' : 'Privatperson'}
        actions={
          <>
            <button type="button" className="ll-btn ll-btn--secondary" onClick={handleEditCustomer}>
              <Edit3 />
              Redigera
            </button>
            <button type="button" className="ll-btn ll-btn--primary" onClick={newInvoice}>
              <Plus />
              Ny faktura
            </button>
          </>
        }
      >
        {(customer.orgnumber || customer.vatnumber || customer.country) && (
          <div className="cust-detail__meta">
            {isCompany && customer.orgnumber && <span>Org.nr {customer.orgnumber}</span>}
            {isCompany && customer.vatnumber && <span>VAT {customer.vatnumber}</span>}
            {customer.country && <span>{customer.country}</span>}
          </div>
        )}
      </PageHeader>

      <div className="cust-detail">
        <div className="cust-detail__main">
          <Section id="cust-invoices" title="Fakturor">
            {invoices.length > 0 ? (
              <>
                <dl className="cust-detail__summary">
                  <div>
                    <dt>Fakturerat</dt>
                    <dd>{formatSek(statistics.totalEarned)}</dd>
                  </div>
                  <div>
                    <dt>Fakturor</dt>
                    <dd>{statistics.invoiceCount}</dd>
                  </div>
                  <div>
                    <dt>Obetalda</dt>
                    <dd>{statistics.pendingInvoices}</dd>
                  </div>
                  <div className={statistics.overdueInvoices > 0 ? 'is-danger' : undefined}>
                    <dt>Förfallna</dt>
                    <dd>{statistics.overdueInvoices}</dd>
                  </div>
                </dl>
                <div className="cust-detail__table">
                  <InvoicesTable invoices={invoices} companyId={companyId} />
                </div>
              </>
            ) : (
              <SectionEmpty
                action={
                  <button type="button" className="ll-btn ll-btn--secondary ll-btn--sm" onClick={newInvoice}>
                    <FileText />
                    Skapa första fakturan
                  </button>
                }
              >
                Det finns inga fakturor till den här kunden ännu.
              </SectionEmpty>
            )}
          </Section>
        </div>

        <aside className="cust-detail__aside">
          <Section id="cust-facts" title="Uppgifter">
            <Facts tight>
              <Fact label="Telefon">
                {customer.phone ? <a href={`tel:${customer.phone}`}>{customer.phone}</a> : null}
              </Fact>
              <Fact label="E-post">
                {customer.email ? <a href={`mailto:${customer.email}`}>{customer.email}</a> : null}
              </Fact>
              <Fact label="Betalningsvillkor">
                {customer.payment_terms ? `${customer.payment_terms} dagar` : null}
              </Fact>
              <Fact label="Land">{customer.country}</Fact>
              {isCompany && <Fact label="Organisationsnummer">{customer.orgnumber}</Fact>}
              {isCompany && <Fact label="VAT-nummer">{customer.vatnumber}</Fact>}
              {Boolean(Number(customer.is_construction_business || 0)) && (
                <Fact label="Moms" wide>Byggföretag — omvänd skattskyldighet</Fact>
              )}
            </Facts>
          </Section>

          <Section
            id="cust-addresses"
            title="Adresser"
            actions={
              <button type="button" className="ll-btn ll-btn--ghost ll-btn--sm" onClick={handleOpenAddAddress}>
                <Plus />
                Lägg till
              </button>
            }
          >
            {addresses.length > 0 ? (
              <ul className="cust-detail__items">
                {addresses.map((address, index) => (
                  <li key={address.address_id || index} className="cust-detail__item">
                    <div className="cust-detail__item-kind">{addressKind(address.address_type)}</div>
                    <div>{address.street1}{address.street2 ? `, ${address.street2}` : ''}</div>
                    <div>{address.postalcode} {address.city}</div>
                    {address.country && <div className="cust-detail__item-sub">{address.country}</div>}
                    <div className="cust-detail__item-actions">
                      <button type="button" onClick={() => handleOpenEditAddress(address)} title="Redigera adress" aria-label="Redigera adress">
                        <Edit3 size={14} />
                      </button>
                      <button type="button" className="is-danger" onClick={() => handleDeleteAddress(address.address_id)} title="Ta bort adress" aria-label="Ta bort adress">
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            ) : (
              <SectionEmpty>Inga adresser har lagts till ännu.</SectionEmpty>
            )}
          </Section>

          {isCompany && (
            <Section
              id="cust-contacts"
              title="Kontaktpersoner"
              actions={
                <button type="button" className="ll-btn ll-btn--ghost ll-btn--sm" onClick={handleOpenAddContact}>
                  <Plus />
                  Lägg till
                </button>
              }
            >
              {contacts.length > 0 ? (
                <ul className="cust-detail__items">
                  {contacts.map((contact, index) => (
                    <li key={contact.contact_id || index} className="cust-detail__item">
                      {contact.role && <div className="cust-detail__item-kind">{contact.role}</div>}
                      <div className="cust-detail__item-name">{contact.contact_name || 'Namn saknas'}</div>
                      {contact.contact_email && (
                        <div className="cust-detail__item-sub">
                          <a href={`mailto:${contact.contact_email}`}>{contact.contact_email}</a>
                        </div>
                      )}
                      {contact.contact_phone && (
                        <div className="cust-detail__item-sub">
                          <a href={`tel:${contact.contact_phone}`}>{contact.contact_phone}</a>
                        </div>
                      )}
                      <div className="cust-detail__item-actions">
                        <button type="button" onClick={() => handleOpenEditContact(contact)} title="Redigera kontaktperson" aria-label="Redigera kontaktperson">
                          <Edit3 size={14} />
                        </button>
                        <button type="button" className="is-danger" onClick={() => handleDeleteContact(contact.contact_id)} title="Ta bort kontaktperson" aria-label="Ta bort kontaktperson">
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </li>
                  ))}
                </ul>
              ) : (
                <SectionEmpty>Inga kontaktpersoner har lagts till ännu.</SectionEmpty>
              )}
            </Section>
          )}
        </aside>
      </div>

      <AddressModal
        show={showAddressModal}
        onClose={closeAddressModal}
        onSubmit={handleAddressSubmit}
        form={addressForm}
        setForm={setAddressForm}
        isEdit={Boolean(editingAddressId)}
        submitting={submitting}
        customerName={customerName}
      />

      <ContactModal
        show={showContactModal}
        onClose={closeContactModal}
        onSubmit={handleContactSubmit}
        form={contactForm}
        setForm={setContactForm}
        isEdit={Boolean(editingContactId)}
        submitting={submitting}
        customerName={customerName}
      />

      <EditCustomerModal
        show={showEditModal}
        onClose={() => setShowEditModal(false)}
        onSubmit={handleEditSubmit}
        form={editForm}
        setForm={setEditForm}
        submitting={submitting}
      />
    </>
  );
};

export default ViewCustomerDetailed;
